import {useCallback, useEffect, useRef, useState} from "react";

const BOTTOM_THRESHOLD = 4;

export interface AssistantConversationScrollState {
  hasOverflow: boolean;
  isAtBottom: boolean;
}

interface UseAssistantConversationScrollOptions {
  contentResizeKey: unknown;
  onScrollPositionChange?: () => void;
}

function getConversationScrollState(element: HTMLDivElement | null) {
  if (!element) return {distanceFromBottom: 0, hasOverflow: false, isAtBottom: true};

  const distanceFromBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
  const hasOverflow = element.scrollHeight - element.clientHeight > BOTTOM_THRESHOLD;
  const isAtBottom = !hasOverflow || distanceFromBottom <= BOTTOM_THRESHOLD;

  return {distanceFromBottom, hasOverflow, isAtBottom};
}

export function useAssistantConversationScroll({
  contentResizeKey,
  onScrollPositionChange,
}: UseAssistantConversationScrollOptions) {
  const [conversationWidth, setConversationWidth] = useState(0);
  const [scrollState, setScrollState] = useState<AssistantConversationScrollState>({
    hasOverflow: false,
    isAtBottom: true,
  });
  const [isDetachedFromBottom, setIsDetachedFromBottom] = useState(false);
  const conversationRef = useRef<HTMLDivElement>(null);
  const conversationContentRef = useRef<HTMLDivElement>(null);
  const scrollStateRef = useRef(scrollState);
  const stickToBottomRef = useRef(true);
  const programmaticScrollRef = useRef(false);
  const programmaticScrollResetRef = useRef<number | undefined>(undefined);
  const userScrollIntentRef = useRef(false);
  const userScrollIntentResetRef = useRef<number | undefined>(undefined);

  const updateScrollState = useCallback((nextState: AssistantConversationScrollState) => {
    scrollStateRef.current = nextState;
    setScrollState((current) =>
      current.hasOverflow === nextState.hasOverflow && current.isAtBottom === nextState.isAtBottom
        ? current
        : nextState,
    );
  }, []);

  const setStickToBottom = useCallback((nextValue: boolean) => {
    stickToBottomRef.current = nextValue;
    setIsDetachedFromBottom(!nextValue);
  }, []);

  const measureScrollState = useCallback(
    (source = "measure") => {
      const element = conversationRef.current;

      if (element && element.scrollHeight === 0 && element.clientHeight === 0) {
        return scrollStateRef.current;
      }

      const measuredState = getConversationScrollState(element);
      const nextState = {
        hasOverflow: measuredState.hasOverflow,
        isAtBottom: measuredState.isAtBottom,
      };

      if (nextState.isAtBottom) {
        setStickToBottom(true);
      } else if (source === "onScroll" && userScrollIntentRef.current) {
        setStickToBottom(false);
      }

      updateScrollState(nextState);

      return nextState;
    },
    [setStickToBottom, updateScrollState],
  );

  const scrollToBottom = useCallback(
    (behavior: ScrollBehavior = "smooth", source = "scroll") => {
      void source;

      const element = conversationRef.current;

      if (!element) return;
      if (element.scrollHeight === 0 && element.clientHeight === 0) {
        return;
      }

      const top = Math.max(0, element.scrollHeight - element.clientHeight);

      programmaticScrollRef.current = true;

      if (programmaticScrollResetRef.current) {
        window.clearTimeout(programmaticScrollResetRef.current);
      }

      if (behavior === "auto") element.scrollTop = top;
      else element.scrollTo({behavior, top});

      window.requestAnimationFrame(() => {
        const measuredState = getConversationScrollState(element);
        const nextState = {
          hasOverflow: measuredState.hasOverflow,
          isAtBottom: measuredState.isAtBottom,
        };

        updateScrollState(nextState);
      });

      programmaticScrollResetRef.current = window.setTimeout(
        () => {
          programmaticScrollRef.current = false;
          measureScrollState("programmatic-scroll-settled");
        },
        behavior === "smooth" ? 1800 : 0,
      );
    },
    [measureScrollState, updateScrollState],
  );

  const scrollToBottomAndFollow = useCallback(
    (behavior: ScrollBehavior = "smooth", source = "scroll") => {
      setStickToBottom(true);
      scrollToBottom(behavior, source);
    },
    [scrollToBottom, setStickToBottom],
  );

  const syncToBottomIfPinned = useCallback(
    (source: string) => {
      if (stickToBottomRef.current) {
        scrollToBottom("auto", source);

        return;
      }

      measureScrollState(source);
    },
    [measureScrollState, scrollToBottom],
  );

  const markUserScrollIntent = useCallback((source?: string) => {
    void source;

    userScrollIntentRef.current = true;

    if (userScrollIntentResetRef.current) window.clearTimeout(userScrollIntentResetRef.current);

    userScrollIntentResetRef.current = window.setTimeout(() => {
      userScrollIntentRef.current = false;
      userScrollIntentResetRef.current = undefined;
    }, 600);
  }, []);

  const handleConversationScroll = useCallback(() => {
    const nextState = measureScrollState("onScroll");

    if (programmaticScrollRef.current && nextState.isAtBottom) {
      programmaticScrollRef.current = false;
      if (programmaticScrollResetRef.current) {
        window.clearTimeout(programmaticScrollResetRef.current);
        programmaticScrollResetRef.current = undefined;
      }
    }

    onScrollPositionChange?.();
  }, [measureScrollState, onScrollPositionChange]);

  useEffect(() => {
    return () => {
      if (programmaticScrollResetRef.current) {
        window.clearTimeout(programmaticScrollResetRef.current);
      }
      if (userScrollIntentResetRef.current) {
        window.clearTimeout(userScrollIntentResetRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const element = conversationRef.current;

    if (!element) return;

    const measureWidth = () => setConversationWidth(element.clientWidth);

    measureWidth();

    if (typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(() => {
      measureWidth();
      syncToBottomIfPinned("root-resize");
      onScrollPositionChange?.();
    });

    observer.observe(element);

    return () => observer.disconnect();
  }, [onScrollPositionChange, syncToBottomIfPinned]);

  useEffect(() => {
    const element = conversationContentRef.current;

    if (!element || typeof ResizeObserver === "undefined") {
      measureScrollState("content-resize-missing-observer");

      return;
    }

    const observer = new ResizeObserver(() => {
      syncToBottomIfPinned("content-resize");
      onScrollPositionChange?.();
    });

    observer.observe(element);

    return () => observer.disconnect();
  }, [contentResizeKey, onScrollPositionChange, syncToBottomIfPinned]);

  useEffect(() => {
    if (stickToBottomRef.current || scrollStateRef.current.isAtBottom) {
      scrollToBottom("auto", "messages-effect");
    }
  }, [contentResizeKey, scrollToBottom]);

  return {
    conversationContentRef,
    conversationRef,
    conversationWidth,
    handleConversationScroll,
    isDetachedFromBottom,
    markUserScrollIntent,
    scrollState,
    scrollToBottom,
    scrollToBottomAndFollow,
  };
}
